import { Component, type ReactElement } from "react";
import { connect } from "react-redux";

import { type IPlaybookPage } from "../../../app-utils/playbook.js";
import { type GenomeAssembly, type InputNcbiParamsInputData } from "../../../graphql.js";
import { initNCBIState, installGenomes } from "../../../redux/modules/ncbi-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { SelectorIcons } from "../../../settings/assets-settings.js";
import PlaybookWizard from "../PlaybookWizard.js";
import INCBIAgentReviewScreen from "./NCBIAgentReviewScreen.js";
import NCBIAgentSelectGenomesScreen from "./NCBIAgentSelectGenomesScreen.js";
import NCBIConfigureOneByOneLooper from "./NCBIConfigureOneByOneLooper.js";

interface INCBIAgentWizardOwnProps {
  onClose: () => void;
}

interface INCBIAgentWizardAppStateProps {
  documentationUrl: string | undefined | null;
}

interface INCBIAgentWizardDispatchProps {
  installGenomes: (options: InputNcbiParamsInputData[]) => Promise<GenomeAssembly[]>;
  initNCBIState: () => void;
}

export interface INCBIAgentWizardProps
  extends INCBIAgentWizardAppStateProps,
    INCBIAgentWizardDispatchProps,
    INCBIAgentWizardOwnProps {}

const SELECT_SCREEN = "SELECT_SCREEN";
const CONFIGURE_SCREEN = "CONFIGURE_SCREEN";
const REVIEW_SCREEN = "REVIEW_SCREEN";

const ncbiAgentScreens: IPlaybookPage[] = [
  {
    description: "Select a genome for import from NCBI",
    pageIdentifier: SELECT_SCREEN,
    title: "Choose a Genome",
  },
  {
    description: "Accept the selections below or make changes for how this genome will be represented in Selector",
    pageIdentifier: CONFIGURE_SCREEN,
    title: "Configure the Genome",
  },
  {
    description: "Check your selection",
    pageIdentifier: REVIEW_SCREEN,
    title: "Review",
  },
];

function mapStateToProps(state: IAppState): INCBIAgentWizardAppStateProps {
  return {
    documentationUrl: state.config.configuration?.application?.documentationUrl,
  };
}

function mapDispatchToProps(dispatch): INCBIAgentWizardDispatchProps {
  return {
    installGenomes: (options: InputNcbiParamsInputData[]) => dispatch(installGenomes(options)),
    initNCBIState: () => dispatch(initNCBIState()),
  };
}

class NCBIAgentWizardImpl extends Component<INCBIAgentWizardProps> {
  constructor(props: INCBIAgentWizardProps) {
    super(props);
  }

  componentDidMount() {
    this.props.initNCBIState();
  }

  render(): ReactElement {
    return (
      <PlaybookWizard
        playbook
        title={"NCBI Agent Configuration"}
        helpLink={`${this.props.documentationUrl}Administrator%2bTools`}
        className={"sample-registration-wizard"}
        steps={ncbiAgentScreens}
        mapStepToScreen={this.mapStepToScreen}
        isCurrentPageLooper={this.isCurrentPageLooper}
        onClose={this.props.onClose}
        icon={SelectorIcons.CONFIG}
      />
    );
  }

  mapStepToScreen = (page: IPlaybookPage) => {
    switch (page.pageIdentifier) {
      case SELECT_SCREEN:
        return <NCBIAgentSelectGenomesScreen playbookPage={page} />;
      case CONFIGURE_SCREEN:
        return <NCBIConfigureOneByOneLooper playbookPage={page} />;
      case REVIEW_SCREEN:
        return <INCBIAgentReviewScreen onDownLoad={this.props.installGenomes} playbookPage={page} />;
    }
  };

  isCurrentPageLooper = (page: IPlaybookPage): boolean => {
    return page.pageIdentifier === CONFIGURE_SCREEN;
  };
}

const NCBIAgentWizard = connect<INCBIAgentWizardAppStateProps, INCBIAgentWizardDispatchProps, INCBIAgentWizardOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(NCBIAgentWizardImpl);

export { NCBIAgentWizard };
export default NCBIAgentWizard;
